import type { ComponentType } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { LockWarning } from '@makemydeal/dr-dash-components';
import { scopedActionsCreators, scopedStateSelectors } from '@makemydeal/shared-scoped-store';

import { BaseDealSlideOutProvider } from '../baseDealSlideouts/BaseDealSlideOutProvider';
import { BaseDealDeskingCardSkeletonLoader } from '../baseDealActivity/baseDealContainer/BaseDealDeskingCardSkeletonLoader';
import { ScenarioActionsRow } from './header/ScenarioActionsRow';
import { ScenarioAiRow } from './header/ScenarioAiRow';
import { ScenarioBanners } from './header/ScenarioBanners';
import { ScenarioDealHeader } from './header/ScenarioDealHeader';
import { ScenarioVehicleBlock } from './header/ScenarioVehicleBlock';
import { ScenarioRows } from './rows/ScenarioRows';
import { ColumnSection, PinnedColumnSection, ScenarioColumnRoot } from './ScenarioGrid.style';

type ScenarioColumnSection = {
    key: string;
    Content: ComponentType<{ scopeId: string }>;
    isPinned?: boolean;
};

export const SCENARIO_COLUMN_SECTIONS: readonly ScenarioColumnSection[] = [
    { key: 'actions', Content: ScenarioActionsRow },
    { key: 'lockWarning', Content: LockWarning },
    { key: 'banners', Content: ScenarioBanners },
    { key: 'aiInsights', Content: ScenarioAiRow },
    { key: 'vehicle', Content: ScenarioVehicleBlock },
    { key: 'dealHeader', Content: ScenarioDealHeader, isPinned: true }
];

export const ScenarioColumn = ({ scopeId }: { scopeId: string }) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const primaryScopeId = useSelector(scopedStateSelectors.getPrimaryScopeId);
    const focusedScopeId = useSelector(scopedStateSelectors.getFocusedScopeId);
    const initializedScopes = useSelector(scopedStateSelectors.getInitializedScopesOrder);
    const isInitialized = initializedScopes.includes(scopeId);

    const columnProps = {
        theme,
        $isPrimary: primaryScopeId === scopeId,
        'data-primary': primaryScopeId === scopeId,
        'data-scope-container': true,
        'data-testid': `scenario-column-${scopeId}`
    };

    if (!isInitialized) {
        return (
            <ScenarioColumnRoot {...columnProps}>
                <ColumnSection style={{ gridRow: '1 / -1' }}>
                    <BaseDealDeskingCardSkeletonLoader />
                </ColumnSection>
            </ScenarioColumnRoot>
        );
    }

    const handleFocus = () => {
        if (focusedScopeId !== scopeId) {
            dispatch(scopedActionsCreators.setFocusedScopeId(scopeId));
        }
    };

    return (
        <BaseDealSlideOutProvider scopeId={scopeId}>
            <ScenarioColumnRoot {...columnProps} onFocus={handleFocus} onClick={handleFocus}>
                {SCENARIO_COLUMN_SECTIONS.map(({ key, Content, isPinned }) =>
                    isPinned ? (
                        <PinnedColumnSection key={key} theme={theme} data-pinned-header>
                            <Content scopeId={scopeId} />
                        </PinnedColumnSection>
                    ) : (
                        <ColumnSection key={key}>
                            <Content scopeId={scopeId} />
                        </ColumnSection>
                    )
                )}
                <ScenarioRows />
            </ScenarioColumnRoot>
        </BaseDealSlideOutProvider>
    );
};
